import {
  Box,
  Card,
  CardContent,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { FC, memo } from 'react';
import { LaneManualAssignment, ShipmentOrderLaneDetail } from 'src/entities/ShipmentOrderLane';
import { useDriversQuery } from 'src/hooks/useDrivers.query';
import { usePartnerCompaniesRequest } from 'src/hooks/usePartnerCompanies.request';

import { EditableAssignment } from '../hooks/useLaneDetailEdit';

import LaneManualAssignments from './LaneManualAssignments';

type Props = {
  detail: ShipmentOrderLaneDetail;
  isEditing?: boolean;
  requiredTruckCount?: number | null;
  manualAssignments?: EditableAssignment[];
  onRequiredTruckCountChange?: (value: number | null) => void;
  onUpdateAssignment?: (tempId: string, field: keyof Omit<LaneManualAssignment, 'id'>, value: string | number | null) => void;
  onAddAssignment?: () => void;
  onRemoveAssignment?: (tempId: string) => void;
  setTabValue: (value: number) => void;
  startOn: string;
};

/**
 * レーン詳細 - 配車情報タブ
 */
const LaneDetailAssignment: FC<Props> = memo(({
  detail,
  isEditing = false,
  requiredTruckCount,
  manualAssignments,
  onRequiredTruckCountChange,
  onUpdateAssignment,
  setTabValue,
  startOn,
}) => {
  const { data: partnerCompanyData } = usePartnerCompaniesRequest(0, '', '', true, 5000);
  const { data: driverData } = useDriversQuery(startOn, { enabled: isEditing });

  // 表示用データ（編集時は編集データ、それ以外は詳細データを使用）
  const displayRequiredTruckCount = isEditing ? requiredTruckCount : detail.requiredTruckCount;
  const displayAssignments = isEditing
    ? manualAssignments || []
    : (detail.laneManualAssignments || []).map((a) => ({ ...a, tempId: `display-${a.id}` }));

  // 手配済台数と残台数を計算
  const assignedCount = isEditing ? displayAssignments.filter((it) => it.carrierCompany && it.truckNumber && it.driverName).length : detail.assignedTruckCount;
  const percentage = displayRequiredTruckCount && displayRequiredTruckCount > 0
    ? Math.round((assignedCount / displayRequiredTruckCount) * 100)
    : 0;

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        {/* 配車サマリー */}
        <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
          配車管理
        </Typography>
        <Box sx={{ gap: 4, border: '1px solid', borderRadius: 1, p: 2, justifyItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" color="primary.main" mb={2} textAlign="center">
              レーン全体物量
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, 180px)' },
              gridAutoRows: '70px',
            }}
          >
            <Card sx={{ textAlign: 'center' }}>
              <CardContent>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {detail.volumeSummary.packages.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  数量
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ textAlign: 'center' }}>
              <CardContent>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {detail.volumeSummary.weightKg.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  重量(kg)
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ textAlign: 'center' }}>
              <CardContent>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {detail.volumeSummary.volumeM3.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  体積(m3)
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center' }}>
            <Typography variant="h6" gutterBottom>
              必要台数を入力してください
            </Typography>
            <TextField
              type='number'
              size='small'
              value={displayRequiredTruckCount ?? ''}
              onChange={(e) => {
                const value = e.target.value === '' ? null : Number(e.target.value);
                onRequiredTruckCountChange?.(value);
              }}
              disabled={!isEditing}
              slotProps={{ input: { inputProps: { min: 0, max: 20 } } }}
              sx={{ width: 80 }}
            />
            <Typography variant="body1">台</Typography>
          </Box>
        </Box>
        {requiredTruckCount !== undefined && requiredTruckCount > 0 && (
          <Box sx={{ mb: 4 }}>
            {/* 配車進捗表示 */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  配車進捗
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {assignedCount} / {requiredTruckCount} 台完了
                </Typography>
              </Box>
              <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                <Box
                  sx={{
                    width: `${percentage}%`,
                    bgcolor: 'success.main',
                    height: '100%',
                    borderRadius: 1,
                    transition: 'width 0.3s ease'
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" mt={1}>
                {percentage}% 完了
              </Typography>
            </Box>
          </Box>
        )}

        {/* 車両スロット一覧 */}
        {manualAssignments && manualAssignments.length > 0 && (
          <LaneManualAssignments
            detail={detail}
            isEditing={isEditing}
            manualAssignments={manualAssignments}
            onUpdateAssignment={onUpdateAssignment}
            setTabValue={setTabValue}
            partnerCompanyData={partnerCompanyData}
            driverData={driverData}
          />
        )}
      </Paper>
    </Box>
  );
});

export default LaneDetailAssignment;
